import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import LooseEndsBanner from '../components/LooseEndsBanner';
import { isHalloweenSeason, HalloweenBanner, HalloweenDecor } from '../components/HalloweenDecor';
import { DashboardService } from '../services/dashboardService';
import { NotesService } from '../services/notesService';
import '../styles/pages/dashboard.css';

export default function Dashboard() {
    const { t } = useTranslation();
    const { user, role, authorizedFetch } = useAuth();
    const navigate = useNavigate();
    
    // Dashboard overview state
    const [dashboardData, setDashboardData] = useState({
        todos: [],
        vetAppointments: { today: [], upcoming: [] },
        daycare: { checkedIn: [] },
        hotel: { upcomingBookings: [], currentCheckIns: [] },
        trials: [],
        upcomingTrials: [],
        transportation: [],
        lunch: [],
        upcomingVaccinations: [],
        birthdays: [],
    });
    
    // Daily summary of work left unclosed on previous days
    const [looseEnds, setLooseEnds] = useState(null);

    // Sticky notes state
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [noteError, setNoteError] = useState('');
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showAllNotesModal, setShowAllNotesModal] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [noteForm, setNoteForm] = useState({ title: '', content: '', color: 'yellow' });
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [notesPage, setNotesPage] = useState(1);
    const NOTES_PER_PAGE = 6;
    
    // History modal state
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyTab, setHistoryTab] = useState('completed');
    const [completedNotes, setCompletedNotes] = useState([]);
    const [deletedNotes, setDeletedNotes] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);

    // Seasonal dressing — decorations only, evaluated once per mount
    const halloween = isHalloweenSeason();

    // Birthday banner state for Miss Karlita (active until 8 PM today, Aug 18, 2026)
    const [showBirthdayBanner, setShowBirthdayBanner] = useState(() => {
        const now = new Date();
        return now.getFullYear() === 2026 && now.getMonth() === 7 && now.getDate() === 18 && now.getHours() < 20;
    });

    useEffect(() => {
        const checkBirthdayBannerTime = () => {
            const now = new Date();
            const isActive = now.getFullYear() === 2026 && now.getMonth() === 7 && now.getDate() === 18 && now.getHours() < 20;
            if (!isActive) {
                setShowBirthdayBanner(false);
            }
        };
        checkBirthdayBannerTime();
        const timer = setInterval(checkBirthdayBannerTime, 60000);
        return () => clearInterval(timer);
    }, []);
    
    // Fetch dashboard overview data
    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!authorizedFetch) return;
            try {
                const data = await DashboardService.getDashboardOverview(authorizedFetch);
                setDashboardData(data);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
            }
        };

        fetchDashboardData();
    }, [authorizedFetch]);

    // Pendientes left open on previous days — loaded on its own so a failure
    // here never takes the rest of the dashboard with it
    useEffect(() => {
        if (!authorizedFetch) return;
        DashboardService.getLooseEnds(authorizedFetch).then(setLooseEnds);
    }, [authorizedFetch]);
    
    // Fetch notes
    useEffect(() => {
        const fetchNotes = async () => {
            if (!authorizedFetch) return;
            setNotesLoading(true);
            try {
                const data = await NotesService.getAllNotes(authorizedFetch);
                setNotes(data);
            } catch (err) {
                console.error('Error fetching notes:', err);
                setNoteError(err.message || 'Error al cargar notas');
            } finally {
                setNotesLoading(false);
            }
        };
        fetchNotes();
    }, [authorizedFetch]);
    
    // Fetch history notes when modal opens or tab changes
    useEffect(() => {
        const fetchHistory = async () => {
            if (!showHistoryModal || !authorizedFetch) return;
            setHistoryLoading(true);
            try {
                if (historyTab === 'completed') {
                    const data = await NotesService.getAllNotes(authorizedFetch, 'completed');
                    setCompletedNotes(data);
                } else {
                    const data = await NotesService.getAllNotes(authorizedFetch, 'deleted');
                    setDeletedNotes(data);
                }
            } catch (err) {
                console.error('Error fetching history notes:', err);
            } finally {
                setHistoryLoading(false);
            }
        };
        fetchHistory();
    }, [showHistoryModal, historyTab, authorizedFetch]);
    
    // Note CRUD handlers
    const openNewNote = () => {
        setEditingNote(null);
        setNoteForm({ title: '', content: '', color: 'yellow' });
        setShowNoteModal(true);
    };
    
    const openEditNote = (note) => {
        setEditingNote(note);
        setNoteForm({ title: note.title, content: note.content, color: note.color });
        setShowNoteModal(true);
    };
    
    const closeNoteModal = () => {
        setShowNoteModal(false);
        setEditingNote(null);
        setNoteForm({ title: '', content: '', color: 'yellow' });
    };
    
    const handleSaveNote = async () => {
        if (!noteForm.title.trim() || !noteForm.content.trim()) return;
        try {
            if (editingNote) {
                const updated = await NotesService.updateNote(authorizedFetch, editingNote._id, noteForm);
                setNotes(prev => prev.map(n => n._id === updated._id ? updated : n));
            } else {
                const created = await NotesService.createNote(authorizedFetch, noteForm);
                setNotes(prev => [created, ...prev]);
            }
            closeNoteModal();
        } catch (err) {
            console.error('Error saving note:', err);
            setNoteError(err.message || 'Error al guardar la nota');
        }
    };
    
    const handleDeleteNote = async (noteId) => {
        try {
            await NotesService.deleteNote(authorizedFetch, noteId);
            setNotes(prev => prev.filter(n => n._id !== noteId));
            setDeleteConfirmId(null);
        } catch (err) {
            console.error('Error deleting note:', err);
            setNoteError(err.message || 'Error al eliminar la nota');
        }
    };
    
    const handleToggleComplete = async (noteId) => {
        try {
            const updated = await NotesService.toggleComplete(authorizedFetch, noteId);
            // Note marked complete → remove from active list
            if (updated.completed) {
                setNotes(prev => prev.filter(n => n._id !== noteId));
            } else {
                setNotes(prev => prev.map(n => n._id === updated._id ? updated : n));
            }
        } catch (err) {
            console.error('Error toggling note:', err);
            setNoteError(err.message || 'Error al actualizar la nota');
        }
    };
    
    const handleRestoreNote = async (noteId) => {
        try {
            const restored = await NotesService.restoreNote(authorizedFetch, noteId);
            // Remove from history lists
            setCompletedNotes(prev => prev.filter(n => n._id !== noteId));
            setDeletedNotes(prev => prev.filter(n => n._id !== noteId));
            // Add back to active notes (avoid dupes)
            setNotes(prev => [restored, ...prev.filter(n => n._id !== noteId)]);
            setHistoryPage(p => Math.max(1, Math.min(p, Math.ceil((historyNotesList.length - 1) / NOTES_PER_PAGE) || 1)));
            setNoteError('');
        } catch (err) {
            console.error('Error restoring note:', err);
            setNoteError(err.message || 'Error al restaurar la nota');
        }
    };

    const openHistory = () => {
        setHistoryTab('completed');
        setHistoryPage(1);
        setShowHistoryModal(true);
    };
    
    const NOTE_COLORS = {
        yellow: '#FFF4B8',
        pink: '#FFD7E5',
        blue: '#CFE3FF',
        green: '#CDF1DC',
        purple: '#E2D6FF',
        orange: '#FFE0B2',
    };
    
    // Pagination helpers
    const totalNotesPages = Math.max(1, Math.ceil(notes.length / NOTES_PER_PAGE));
    const paginatedNotes = notes.slice((notesPage - 1) * NOTES_PER_PAGE, notesPage * NOTES_PER_PAGE);
    
    const historyNotesList = historyTab === 'completed' ? completedNotes : deletedNotes;
    const totalHistoryPages = Math.max(1, Math.ceil(historyNotesList.length / NOTES_PER_PAGE));
    const paginatedHistory = historyNotesList.slice((historyPage - 1) * NOTES_PER_PAGE, historyPage * NOTES_PER_PAGE);
    
    // Format note date/time
    const formatNoteDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
        }) + ' ' + date.toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit'
        });
    };
    
    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return t('no_date');
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return t('invalid_date');
            }

            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }) + ' ' + date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return t('invalid_date');
        }
    };
    
    // Format time for display
    const formatTime = (timeString) => {
        if (!timeString) return t('no_time');
        
        try {
            // Handle different time formats
            if (timeString.includes(':')) {
                // If it's already in HH:MM format
                const [hours, minutes] = timeString.split(':');
                const date = new Date();
                date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                
                if (isNaN(date.getTime())) {
                    return t('invalid_time');
                }
                
                return date.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } else {
                // Try parsing as a full date/time string
                const date = new Date(timeString);
                if (isNaN(date.getTime())) {
                    return t('invalid_time');
                }
                
                return date.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        } catch (error) {
            return t('invalid_time');
        }
    };

    // Mobile hero helpers
    const today = new Date();
    const heroDate = today.toLocaleDateString('es-ES', { weekday: 'long', month: 'short', day: 'numeric' });
    const hour = today.getHours();
    const greeting = hour < 12 ? 'Buenos días' : hour < 17 ? 'Buenas tardes' : 'Buenas noches';
    const daycareCount = dashboardData.daycare.checkedIn.length;
    const hotelCount = dashboardData.hotel.currentCheckIns.length;
    const vetCount = dashboardData.vetAppointments.today.length;

    const vaccDayLabel = (iso) => {
        const d = new Date(iso);
        const now = new Date();
        const diffDays = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
        if (diffDays === 0) return 'Hoy';
        if (diffDays === 1) return 'Mañana';
        return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
    };

    // Combined today's activity list for mobile
    const todayActivity = [
        ...dashboardData.daycare.checkedIn.slice(0, 4).map(v => ({
            type: 'checkin',
            icon: '📥',
            avatarClass: 'avatar--in',
            name: v.dog?.name || t('unknown_dog'),
            detail: [v.dog?.breed, v.dog?.owners?.[0]?.name].filter(Boolean).join(' · '),
            badge: 'Entrada',
            badgeClass: 'badge--in',
            time: v.checkInTime ? formatTime(v.checkInTime) : null,
            diaSuelto: v.visitType !== 'trial' && (!v.package || v.package?.packageType === '1-day'),
        })),
        ...dashboardData.vetAppointments.today.slice(0, 3).map(a => ({
            type: 'vet',
            icon: '🩺',
            avatarClass: 'avatar--vet',
            // A walk-in has no dog record — the name the visitor gave is all there is
            name: a.dog?.name || a.walkInInfo?.dogName || t('unknown_dog'),
            detail: [a.dog?.breed || a.walkInInfo?.fullName, a.reason || a.cause || 'Cita vet.'].filter(Boolean).join(' · '),
            badge: 'Vet',
            badgeClass: 'badge--vet',
            time: a.time ? formatTime(a.time) : null,
        })),
        ...dashboardData.hotel.currentCheckIns.slice(0, 2).map(b => ({
            type: 'hotel',
            icon: '🏨',
            avatarClass: 'avatar--hotel',
            name: b.dog?.name || t('unknown_dog'),
            detail: [b.dog?.breed, b.dog?.owners?.[0]?.name].filter(Boolean).join(' · '),
            badge: 'Hotel',
            badgeClass: 'badge--hotel',
            time: null,
        })),
        ...(dashboardData.trials || []).slice(0, 3).map(tr => {
            const d = new Date(tr.scheduledDate);
            const isToday = d.toDateString() === new Date().toDateString();
            return {
                type: 'trial',
                icon: '🎟️',
                avatarClass: 'avatar--trial',
                name: tr.dogName,
                detail: [tr.dogBreed, tr.ownerName].filter(Boolean).join(' · '),
                badge: isToday ? 'Prueba hoy' : 'Prueba',
                badgeClass: 'badge--trial',
                time: isToday ? null : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
            };
        }),
    ];

    return (
        <div className="dashboard-container">
            <Navigation />

            <main className={`dashboard-main${halloween ? ' halloween' : ''}`}>
                {halloween && <HalloweenDecor />}
                {/* Birthday Banner for Miss Karlita */}
                {showBirthdayBanner && (
                    <div className="birthday-banner">
                        <div className="birthday-banner-content">
                            <span className="birthday-icon">🎂</span>
                            <div className="birthday-text">
                                <div className="birthday-title">🎉 ¡Feliz Cumpleaños Miss Karlita! 🎂</div>
                                <div className="birthday-subtitle">
                                    ¡De parte de todo el equipo de Happy Tails te deseamos un día súper especial lleno de alegría, amor y muchos apapachos perrunos! 🐾✨🎈
                                </div>
                            </div>
                        </div>
                        <button
                            className="birthday-close-btn"
                            onClick={() => setShowBirthdayBanner(false)}
                            aria-label="Cerrar banner"
                            title="Cerrar"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {/* Halloween greeting — disappears on its own after Nov 2 */}
                {halloween && <HalloweenBanner />}

                {/* Mobile hero — replaces welcome section on phones */}
                <div className="mobile-hero">
                    <div className="hero-bg-paw">{halloween ? '🎃' : '🐾'}</div>
                    <p className="mobile-hero-date">{heroDate}</p>
                    <h2 className="mobile-hero-title">{greeting}, {user?.username || role} 👋</h2>
                    <div className="mobile-hero-stats">
                        <div className="mobile-stat-pill">
                            <span className="mobile-stat-num">{daycareCount}</span>
                            <span className="mobile-stat-lbl">Entradas hoy</span>
                        </div>
                        <div className="mobile-stat-pill">
                            <span className="mobile-stat-num">{hotelCount}</span>
                            <span className="mobile-stat-lbl">Huéspedes hotel</span>
                        </div>
                        <div className="mobile-stat-pill">
                            <span className="mobile-stat-num">{vetCount}</span>
                            <span className="mobile-stat-lbl">Citas vet.</span>
                        </div>
                    </div>
                </div>

                {/* Mobile Quick Actions — phones only */}
                <div className="mobile-quick-section">
                    <div className="mobile-section-row">
                        <p className="mobile-section-head">Acciones rápidas</p>
                    </div>
                    <div className="mobile-quick-grid">
                        <button className="mobile-qa-btn" onClick={() => navigate('/daycare')}>
                            <div className="mobile-qa-icon qa-purple">📥</div>
                            <span className="mobile-qa-label">Entrada</span>
                        </button>
                        <button className="mobile-qa-btn" onClick={() => navigate('/daycare')}>
                            <div className="mobile-qa-icon qa-warm">📤</div>
                            <span className="mobile-qa-label">Salida</span>
                        </button>
                        <button className="mobile-qa-btn" onClick={() => navigate('/vet')}>
                            <div className="mobile-qa-icon qa-mint">🩺</div>
                            <span className="mobile-qa-label">Cita vet.</span>
                        </button>
                        <button className="mobile-qa-btn" onClick={openNewNote}>
                            <div className="mobile-qa-icon qa-pink">📝</div>
                            <span className="mobile-qa-label">Nueva nota</span>
                        </button>
                        <button className="mobile-qa-btn" onClick={() => navigate('/pos/new')}>
                            <div className="mobile-qa-icon qa-warm">💳</div>
                            <span className="mobile-qa-label">Nueva venta</span>
                        </button>
                    </div>
                </div>

                {/* Desktop welcome section — hidden on phones */}
                <div className="welcome-section desktop-welcome">
                    <h1>
                        {halloween
                            ? <><span className="hw-emoji">🎃</span> {t('welcome')} <span className="hw-emoji">👻</span></>
                            : <>🐾 {t('welcome')} 🐾</>}
                    </h1>
                    <p className="welcome-subtitle">{t('welcome_subtitle')}</p>
                    <p className="user-info">{t('logged_in_as')} <strong>{user?.username || user?.name}</strong></p>
                </div>

                <LooseEndsBanner summary={looseEnds} />

                <div className="main-navigation">
                    <h2 className="desktop-welcome">Acceso rápido</h2>
                    <p className="mobile-section-head">Servicios</p>
                    <div className="dashboard-cards">
                        <Link to="/dogs" className="main-card-link">
                            <div className="main-card dogs-card">
                                <div className="card-icon">
                                    🐾
                                </div>
                                <div className="card-content">
                                    <h3>Perros</h3>
                                    <p>{t('dogs_desc')}</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>

                        <Link to="/daycare" className="main-card-link">
                            <div className="main-card daycare-card">
                                <div className="card-icon">
                                    🌸
                                </div>
                                <div className="card-content">
                                    <h3>Guardería</h3>
                                    <p>{t('daycare_desc')}</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>

                        <Link to="/hotel" className="main-card-link">
                            <div className="main-card hotel-card">
                                <div className="card-icon">
                                    🏢
                                </div>
                                <div className="card-content">
                                    <h3>Hotel</h3>
                                    <p>{t('hotel_desc')}</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>

                        <Link to="/vet" className="main-card-link">
                            <div className="main-card vet-card">
                                <div className="card-icon">
                                    🩺
                                </div>
                                <div className="card-content">
                                    <h3>Veterinaria</h3>
                                    <p>{t('veterinary_desc')}</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>

                        <Link to="/grooming" className="main-card-link">
                            <div className="main-card grooming-card">
                                <div className="card-icon">
                                    ✂️
                                </div>
                                <div className="card-content">
                                    <h3>Estética</h3>
                                    <p>{t('grooming_desc')}</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>

                        <Link to="/pos" className="main-card-link">
                            <div className="main-card sales-card">
                                <div className="card-icon">💳</div>
                                <div className="card-content">
                                    <h3>Ventas</h3>
                                    <p>Punto de venta y registro de cobros</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>

                        <Link to="/happy-bus" className="main-card-link">
                            <div className="main-card bus-card">
                                <div className="card-icon">🚐</div>
                                <div className="card-content">
                                    <h3>Happy Bus</h3>
                                    <p>Gestión de rutas y transporte de mascotas</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>

                        <button className="main-card-link quick-note-card-btn" onClick={openNewNote}>
                            <div className="main-card quick-note-card">
                                <div className="card-icon">📝</div>
                                <div className="card-content">
                                    <h3>Agregar nota</h3>
                                    <p>Nota rápida</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </button>

                        <Link to="/settings" className="main-card-link">
                            <div className="main-card settings-card">
                                <div className="card-icon">⚙️</div>
                                <div className="card-content">
                                    <h3>Configuración</h3>
                                    <p>Ajustes del sistema y administración</p>
                                </div>
                                <div className="card-arrow">→</div>
                            </div>
                        </Link>
                    </div>
                </div>

                {/* Today's Activity — mobile only */}
                {todayActivity.length > 0 && (
                    <div className="mobile-activity-section">
                        <div className="mobile-section-row">
                            <p className="mobile-section-head">Hoy · {todayActivity.length}</p>
                        </div>
                        <div className="mobile-activity-list">
                            {todayActivity.map((item, i) => (
                                <div key={i} className="mobile-activity-card">
                                    <div className={`activity-avatar ${item.avatarClass}`}>{item.icon}</div>
                                    <div className="activity-info">
                                        <span className="activity-name">{item.name}</span>
                                        <span className="activity-detail">{item.detail}</span>
                                    </div>
                                    <div className="activity-right">
                                        <span className={`activity-badge ${item.badgeClass}`}>{item.badge}</span>
                                        {item.diaSuelto && <span className="activity-badge badge--dia-suelto">Día Suelto</span>}
                                        {item.time && <span className="activity-time">{item.time}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Transportation schedule */}
                {dashboardData.transportation.length > 0 && (
                    <div className="mobile-activity-section">
                        <div className="mobile-section-row">
                            <p className="mobile-section-head">🚗 Transporte hoy · {dashboardData.transportation.length}</p>
                        </div>
                        <div className="mobile-activity-list">
                            {dashboardData.transportation.map((item, i) => {
                                const times = [
                                    item.pickUpTime && `🛻 ${item.pickUpTime}`,
                                    item.dropOffTime && `🏠 ${item.dropOffTime}`,
                                ].filter(Boolean).join('  ');
                                const detail = [item.breed, item.ownerName, item.notes].filter(Boolean).join(' · ');
                                return (
                                    <div key={i} className="mobile-activity-card">
                                        <div className="activity-avatar avatar--transport">🚗</div>
                                        <div className="activity-info">
                                            <span className="activity-name">{item.dogName}</span>
                                            {detail && <span className="activity-detail">{detail}</span>}
                                        </div>
                                        <div className="activity-right">
                                            <span className="activity-badge badge--transport">Transporte</span>
                                            {times && <span className="activity-time">{times}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Lunch schedule */}
                {dashboardData.lunch.length > 0 && (
                    <div className="mobile-activity-section">
                        <div className="mobile-section-row">
                            <p className="mobile-section-head">🍽️ Almuerzo hoy · {dashboardData.lunch.length}</p>
                        </div>
                        <div className="mobile-activity-list">
                            {dashboardData.lunch.map((item, i) => {
                                const detail = [item.breed, item.ownerName].filter(Boolean).join(' · ');
                                return (
                                    <div key={i} className="mobile-activity-card">
                                        <div className="activity-avatar avatar--lunch">🍽️</div>
                                        <div className="activity-info">
                                            <span className="activity-name">{item.dogName}</span>
                                            {detail && <span className="activity-detail">{detail}</span>}
                                            {item.instructions && <span className="activity-detail">{item.instructions}</span>}
                                        </div>
                                        <div className="activity-right">
                                            <span className="activity-badge badge--lunch">Almuerzo</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Upcoming Vaccinations */}
                {dashboardData.upcomingVaccinations.length > 0 && (
                    <div className="mobile-activity-section">
                        <div className="mobile-section-row">
                            <p className="mobile-section-head">💉 Vacunas próximas · {dashboardData.upcomingVaccinations.length}</p>
                            <button className="notes-see-all-btn" onClick={() => navigate('/vet')}>Ver todas ›</button>
                        </div>
                        <div className="mobile-activity-list">
                            {dashboardData.upcomingVaccinations.map((a, i) => {
                                const dogName = a.dog?.name || 'Sin nombre';
                                const vaccines = a.vaccines?.length > 0 ? a.vaccines.join(', ') : a.cause;
                                const dayLabel = vaccDayLabel(a.appointmentDate);
                                const timeStr = new Date(a.appointmentDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                                const isSchema = !!a.schemaId;
                                return (
                                    <div key={a._id || i} className="mobile-activity-card">
                                        <div className="activity-avatar avatar--vaccine">💉</div>
                                        <div className="activity-info">
                                            <span className="activity-name">{dogName}</span>
                                            <span className="activity-detail">{vaccines}</span>
                                        </div>
                                        <div className="activity-right">
                                            <span className="activity-badge badge--vaccine">
                                                {dayLabel}
                                            </span>
                                            <span className="activity-time">
                                                {timeStr}{isSchema && ` · P${a.schemaStep}/${a.schemaTotal}`}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Birthdays this month */}
                {dashboardData.birthdays.length > 0 && (
                    <div className="mobile-activity-section">
                        <div className="mobile-section-row">
                            <p className="mobile-section-head">🎂 Cumpleaños del mes · {dashboardData.birthdays.length}</p>
                        </div>
                        <div className="mobile-activity-list">
                            {dashboardData.birthdays.map(dog => (
                                <div
                                    key={dog.dogId}
                                    className={`mobile-activity-card${dog.isToday ? ' birthday-today-card' : ''}`}
                                >
                                    <div className={`activity-avatar${dog.isToday ? ' avatar--birthday-today' : ' avatar--birthday'}`}>
                                        {dog.pictureUrl
                                            ? <img src={dog.pictureUrl} alt={dog.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                            : '🐾'}
                                    </div>
                                    <div className="activity-info">
                                        <span className="activity-name">
                                            {dog.name}
                                            {dog.isToday && <span className="birthday-today-label"> 🎉</span>}
                                        </span>
                                        <span className="activity-detail">
                                            {[dog.breed, dog.ownerName].filter(Boolean).join(' · ')}
                                        </span>
                                    </div>
                                    <div className="activity-right">
                                        <span className={`activity-badge${dog.isToday ? ' badge--birthday-today' : ' badge--birthday'}`}>
                                            {dog.isToday ? '¡Hoy! 🎂' : `Día ${dog.day}`}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Sticky Notes Section — horizontal carousel */}
                <div className="notes-section-wrap">
                    <div className="mobile-section-row">
                        <p className="mobile-section-head">📝 {t('sticky_notes')}</p>
                        <button className="notes-see-all-btn" onClick={() => setShowAllNotesModal(true)}>
                            {t('see_all')} ›
                        </button>
                    </div>
                    {noteError && (
                        <p className="notes-error" style={{ color: 'var(--danger, #e53e3e)', marginBottom: '0.5rem' }}>
                            {noteError}
                        </p>
                    )}
                    {notesLoading ? (
                        <p className="notes-loading">{t('loading')}...</p>
                    ) : (
                        <div className="notes-row">
                            {notes.slice(0, 4).map(note => (
                                <div
                                    key={note._id}
                                    className="note-card"
                                    style={{ backgroundColor: NOTE_COLORS[note.color] || NOTE_COLORS.yellow }}
                                    onClick={() => openEditNote(note)}
                                >
                                    <button
                                        className="note-check-circle"
                                        onClick={e => { e.stopPropagation(); handleToggleComplete(note._id); }}
                                        title={t('mark_complete')}
                                    />
                                    <h4 className="note-card-title">{note.title}</h4>
                                    <p className="note-card-body">{note.content}</p>
                                    <div className="note-card-meta">
                                        <span>{note.createdBy?.username || 'Unknown'}</span>
                                        <span>{formatNoteDate(note.createdAt)}</span>
                                    </div>
                                </div>
                            ))}
                            {notes.length === 0 && (
                                <p className="no-notes-msg">{t('no_notes')}</p>
                            )}
                            <button className="note-card note-card-add" onClick={openNewNote}>
                                + {t('add_note')}
                            </button>
                        </div>
                    )}
                </div>
                
                {/* All Notes Modal */}
                {showAllNotesModal && (
                    <div className="note-modal-overlay" onClick={() => setShowAllNotesModal(false)}>
                        <div className="note-modal history-modal" onClick={e => e.stopPropagation()}>
                            <div className="history-modal-header">
                                <h3>📝 {t('sticky_notes')}</h3>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <button className="history-link" onClick={() => { setShowAllNotesModal(false); openHistory(); }}>
                                        📚 {t('notes_history')}
                                    </button>
                                    <button className="history-close-btn" onClick={() => setShowAllNotesModal(false)}>✕</button>
                                </div>
                            </div>
                            <div className="history-content">
                                {notesLoading ? (
                                    <p className="notes-loading">{t('loading')}...</p>
                                ) : notes.length === 0 ? (
                                    <p className="no-notes-msg">{t('no_notes')}</p>
                                ) : (
                                    <>
                                    <div className="all-notes-grid">
                                        {paginatedNotes.map(note => (
                                            <div
                                                key={note._id}
                                                className="sticky-note-card"
                                                style={{ backgroundColor: NOTE_COLORS[note.color] || NOTE_COLORS.yellow }}
                                            >
                                                <div className="sticky-note-top">
                                                    <label className="note-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={false}
                                                            onChange={() => handleToggleComplete(note._id)}
                                                        />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                    <h4 className="sticky-note-title">{note.title}</h4>
                                                    <div className="sticky-note-actions">
                                                        <button className="note-action-btn edit" onClick={() => { setShowAllNotesModal(false); openEditNote(note); }} title={t('edit')}>✏️</button>
                                                        {deleteConfirmId === note._id ? (
                                                            <span className="delete-confirm">
                                                                <button className="note-action-btn confirm-yes" onClick={() => handleDeleteNote(note._id)}>✓</button>
                                                                <button className="note-action-btn confirm-no" onClick={() => setDeleteConfirmId(null)}>✕</button>
                                                            </span>
                                                        ) : (
                                                            <button className="note-action-btn delete" onClick={() => setDeleteConfirmId(note._id)} title={t('delete')}>🗑️</button>
                                                        )}
                                                    </div>
                                                </div>
                                                <p className="sticky-note-content">{note.content}</p>
                                                <div className="sticky-note-meta">
                                                    <span className="note-date">{formatNoteDate(note.createdAt)}</span>
                                                    <span className="note-creator">{t('created_by')}: {note.createdBy?.username || 'Unknown'}</span>
                                                    {note.updatedBy && note.createdBy && note.updatedBy._id !== note.createdBy._id && (
                                                        <span className="note-editor">{t('edited_by')}: {note.updatedBy?.username || 'Unknown'}{note.updatedAt && ` • ${formatNoteDate(note.updatedAt)}`}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {totalNotesPages > 1 && (
                                        <div className="notes-pagination">
                                            <button disabled={notesPage <= 1} onClick={() => setNotesPage(p => p - 1)}>‹</button>
                                            <span>{t('page')} {notesPage} {t('of')} {totalNotesPages}</span>
                                            <button disabled={notesPage >= totalNotesPages} onClick={() => setNotesPage(p => p + 1)}>›</button>
                                        </div>
                                    )}
                                    </>
                                )}
                            </div>
                            <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                                <button
                                    className="add-note-btn"
                                    style={{ width: '100%' }}
                                    onClick={() => { setShowAllNotesModal(false); openNewNote(); }}
                                >
                                    + {t('add_note')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Note Modal */}
                {showNoteModal && (
                    <div className="note-modal-overlay" onClick={closeNoteModal}>
                        <div className="note-modal" onClick={e => e.stopPropagation()}>
                            <h3>{editingNote ? t('edit_note') : t('new_note')}</h3>
                            <div className="note-form">
                                <label>{t('note_title')}</label>
                                <input
                                    type="text"
                                    value={noteForm.title}
                                    onChange={e => setNoteForm(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder={t('note_title_placeholder')}
                                />
                                <label>{t('note_content')}</label>
                                <textarea
                                    value={noteForm.content}
                                    onChange={e => setNoteForm(prev => ({ ...prev, content: e.target.value }))}
                                    placeholder={t('note_content_placeholder')}
                                    rows={5}
                                />
                                <label>{t('note_color')}</label>
                                <div className="color-picker">
                                    {Object.entries(NOTE_COLORS).map(([name, hex]) => (
                                        <button
                                            key={name}
                                            className={`color-swatch ${noteForm.color === name ? 'selected' : ''}`}
                                            style={{ backgroundColor: hex }}
                                            onClick={() => setNoteForm(prev => ({ ...prev, color: name }))}
                                            title={name}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="note-modal-actions">
                                <button className="note-cancel-btn" onClick={closeNoteModal}>{t('cancel')}</button>
                                <button
                                    className="note-save-btn"
                                    onClick={handleSaveNote}
                                    disabled={!noteForm.title.trim() || !noteForm.content.trim()}
                                >{t('save')}</button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* History Modal */}
                {showHistoryModal && (
                    <div className="note-modal-overlay" onClick={() => setShowHistoryModal(false)}>
                        <div className="note-modal history-modal" onClick={e => e.stopPropagation()}>
                            <div className="history-modal-header">
                                <h3>📚 {t('notes_history')}</h3>
                                <button className="history-close-btn" onClick={() => setShowHistoryModal(false)}>✕</button>
                            </div>
                            <div className="history-tabs">
                                <button
                                    className={`history-tab ${historyTab === 'completed' ? 'active' : ''}`}
                                    onClick={() => { setHistoryTab('completed'); setHistoryPage(1); }}
                                >✅ {t('completed_notes')}</button>
                                <button
                                    className={`history-tab ${historyTab === 'deleted' ? 'active' : ''}`}
                                    onClick={() => { setHistoryTab('deleted'); setHistoryPage(1); }}
                                >🗑️ {t('deleted_notes')}</button>
                            </div>
                            <div className="history-content">
                                {historyLoading ? (
                                    <p className="notes-loading">{t('loading')}...</p>
                                ) : paginatedHistory.length === 0 ? (
                                    <p className="no-notes-msg">
                                        {historyTab === 'completed' ? t('no_completed_notes') : t('no_deleted_notes')}
                                    </p>
                                ) : (
                                    <>
                                    <div className="history-notes-list">
                                        {paginatedHistory.map(note => (
                                            <div
                                                key={note._id}
                                                className="history-note-item"
                                                style={{ borderLeftColor: NOTE_COLORS[note.color] || NOTE_COLORS.yellow }}
                                            >
                                                <div className="history-note-top">
                                                    <h4>{note.title}</h4>
                                                    <span className="history-note-date">{formatNoteDate(note.createdAt)}</span>
                                                </div>
                                                <p className="history-note-content">{note.content}</p>
                                                <div className="history-note-footer">
                                                    <div className="history-note-meta">
                                                        <span>{t('created_by')}: {note.createdBy?.username || 'Unknown'}{note.createdAt && ` • ${formatNoteDate(note.createdAt)}`}</span>
                                                        {historyTab === 'completed' && note.completedBy && (
                                                            <span className="note-completed-by">
                                                                {t('marked_done_by')}: {note.completedBy?.username || 'Unknown'}
                                                                {note.completedAt && ` • ${formatNoteDate(note.completedAt)}`}
                                                            </span>
                                                        )}
                                                        {historyTab === 'deleted' && note.deletedBy && (
                                                            <span className="note-deleted-by">
                                                                {t('deleted_by_label')}: {note.deletedBy?.username || 'Unknown'}
                                                                {note.deletedAt && ` • ${formatNoteDate(note.deletedAt)}`}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="history-restore-btn"
                                                        onClick={() => handleRestoreNote(note._id)}
                                                        title={t('restore_note')}
                                                    >↩️ {t('restore')}</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {totalHistoryPages > 1 && (
                                        <div className="notes-pagination">
                                            <button disabled={historyPage <= 1} onClick={() => setHistoryPage(p => p - 1)}>‹</button>
                                            <span>{t('page')} {historyPage} {t('of')} {totalHistoryPages}</span>
                                            <button disabled={historyPage >= totalHistoryPages} onClick={() => setHistoryPage(p => p + 1)}>›</button>
                                        </div>
                                    )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                
            </main>
        </div>
    );
}
